import { ReactNode } from 'react'
import { IconType } from 'react-icons'
import {
  Box,
  BoxProps,
  Flex,
  forwardRef,
  Link,
  LinkProps,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  VisuallyHidden,
  Card,
  HStack,
  Divider
} from '~/design_system'
import { Icon } from '~/icons/create-icon'

export type StatCardProps = BoxProps & {
  /** The upper left icon */
  icon: ReactNode
  /** The large data element */
  mainStat: ReactNode
  /** The smaller sub data element(s), typically 2, but should be able to support 1–3 */
  subStats: ReactNode
}

/**
 * Displays an icon on a colored background next to the label and number for a
 * primary statistic, with two secondary statistics displayed below.
 */
export const StatCard = ({ icon, mainStat, subStats, children, ...props }: StatCardProps) => {
  return (
    <Card variant="homeCard" spacing={2} {...props}>
      <HStack>
        {icon}
        <Stack flexGrow={1} divider={<Divider borderColor="gray.300" />}>
          <HStack spacing={5}>{mainStat}</HStack>
          {subStats && (
            <HStack spacing={1} flexGrow={1}>
              {subStats}
            </HStack>
          )}
        </Stack>
      </HStack>
    </Card>
  )
}

export type StatCardIconProps = {
  /** The icon for the card */
  icon: typeof Icon | IconType
  /** The color of the icon background box */
  color: string
}

/** A white icon inside a rounded, colored background box */
StatCard.Icon = ({ color, icon, ...props }: StatCardIconProps) => (
  <Flex
    w={8}
    h={8}
    color="white"
    bg={color}
    borderRadius="full"
    p={2}
    justifyContent="center"
    alignItems="center"
    alignSelf="flex-start"
    ml={2}
    mt={3}
    {...props}>
    <Box as={icon as any} w="full" h="full" />
  </Flex>
)
;(StatCard.Icon as any).displayName = 'StatCard.Icon'

const LinkWithHoverEffect = forwardRef<LinkProps, 'a'>(({ href, ...props }, ref) => {
  if (!href) return <>{props.children}</>
  return (
    // @ts-ignore Expression produces a union type that is too complex to represent
    <Link
      href={href}
      variant="homeLink"
      ref={ref}
      position="relative"
      display="block"
      px={2}
      py={1}
      borderRadius="xl"
      flexGrow={1}
      bg="transparent"
      transform="translateZ(0)"
      transition="all .05s ease-out"
      {...props}
    />
  )
})
;(LinkWithHoverEffect as any).displayName = 'LinkWithHoverEffect'

export type StatCardStatProps = LinkProps & {
  /** The label of the stat */
  label: ReactNode
  /** The count for items for this stat */
  stat: ReactNode
  /** A prefix read by screen readers before the label, e.g. "Jobs" + "On hold" */
  ariaPrefix?: ReactNode
}

export type MainStatProps = StatCardStatProps

StatCard.MainStat = ({ label, stat, href, ...props }: MainStatProps) => (
  <LinkWithHoverEffect {...props} href={href} flexGrow={1} display="flex" pb={0}>
    <Stat>
      <StatLabel textStyle="subtext">
        {label}
        <VisuallyHidden as="span">:</VisuallyHidden>
      </StatLabel>
      <StatNumber fontSize="5xl" lineHeight="1.2" color="gray.800">
        {stat}
      </StatNumber>
    </Stat>
  </LinkWithHoverEffect>
)
;(StatCard.MainStat as any).displayName = 'StatCard.MainStat'

StatCard.SubStat = ({ ariaPrefix, label, stat, href, ...props }: StatCardStatProps) => (
  <LinkWithHoverEffect {...props} href={href}>
    <Stat>
      <StatLabel textStyle="subtext" lineHeight="shorter">
        <VisuallyHidden as="span">{ariaPrefix}</VisuallyHidden>
        {label}
        <VisuallyHidden as="span">:</VisuallyHidden>
      </StatLabel>
      <StatNumber fontSize="xl" color="gray.800">
        {stat}
      </StatNumber>
    </Stat>
  </LinkWithHoverEffect>
)
;(StatCard.SubStat as any).displayName = 'StatCard.SubStat'
